import React, { useState, useEffect } from 'react'
import { useResponsive, useSidebarVisibility } from '../hooks/useResponsive.jsx'
import { 
  Menu, 
  X, 
  Home, 
  Calendar, 
  Heart, 
  ShoppingCart, 
  Users, 
  Package, 
  Navigation, 
  BarChart3, 
  MapPin, 
  Video,
  Settings,
  LogOut,
  BookOpen,
  Music2
} from 'lucide-react'

const MobileNavigation = () => {
  const { isMobile, isTablet } = useResponsive()
  const { sidebarOpen, setSidebarOpen, shouldShowMobileMenu } = useSidebarVisibility()
  const [activeSection, setActiveSection] = useState('dashboard')

  // Navegação items
  const navigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, section: 'dashboard' },
    { name: 'Calendário', href: '/calendar', icon: Calendar, section: 'calendar' },
    { name: 'Saúde', href: '/health', icon: Heart, section: 'health' },
    { name: 'Compras', href: '/shopping', icon: ShoppingCart, section: 'shopping' },
    { name: 'Mural da Família', href: '/family-board', icon: Users, section: 'family-board' },
    { name: 'Controle de Itens', href: '/items-control', icon: Package, section: 'items-control' },
    { name: 'Localização da Família', href: '/family-geo', icon: Navigation, section: 'family-geo' },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, section: 'analytics' },
    { name: 'Localizador', href: '/locator', icon: MapPin, section: 'locator' },
    { name: 'Sala de Vídeo', href: '/video-room', icon: Video, section: 'video-room' },
    { name: 'Minha Canção Preferida', href: '/favorite-songs', icon: Music2, section: 'favorite-songs' },
  ]

  // Detectar seção ativa baseada na URL
  useEffect(() => {
    const path = window.location.pathname
    const currentSection = navigationItems.find(item => item.href === path)?.section || 'dashboard'
    setActiveSection(currentSection)
  }, [])

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && !event.target.closest('.mobile-nav')) {
        setSidebarOpen(false)
      }
    }

    if (isMobile) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [sidebarOpen, isMobile, setSidebarOpen])

  // Prevenir scroll quando menu está aberto
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [sidebarOpen, isMobile])

  if (!shouldShowMobileMenu) {
    return null
  }

  return (
    <>
      {/* Botão do menu hambúrguer */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg lg:hidden mobile-nav"
        aria-label="Abrir menu"
      >
        {sidebarOpen ? (
          <X className="w-6 h-6 text-gray-700" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700" />
        )}
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Menu lateral */}
      <div className={`
        fixed top-0 left-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden mobile-nav
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header do menu */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Domus Family</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Navegação */}
        <nav className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = activeSection === item.section
              
              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setActiveSection(item.section)
                    setSidebarOpen(false)
                  }}
                  className={`
                    flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors duration-200
                    ${isActive 
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </a>
              )
            })}
          </div>

          {/* Seção de configurações */}
          <div className="border-t border-gray-200 p-4">
            <div className="space-y-2">
              <a
                href="/guide"
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                <BookOpen className="w-5 h-5" />
                <span className="font-medium">Manual do Usuário</span>
              </a>
              <a
                href="/settings"
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Configurações</span>
              </a>
              <button
                onClick={() => {
                  // Lógica de logout
                  setSidebarOpen(false)
                }}
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors duration-200 w-full text-left"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sair</span>
              </button>
            </div>
          </div>
        </nav>

        {/* Footer do menu */}
        <div className="p-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-500">Domus Family Hub</p>
            <p className="text-xs text-gray-400">v1.0.0</p>
          </div>
        </div>
      </div>
    </>
  )
}

// Componente de navegação inferior (bottom navigation)
export const BottomNavigation = () => {
  const { isMobile } = useResponsive()
  const [activeSection, setActiveSection] = useState('dashboard')

  const bottomNavItems = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, section: 'dashboard' },
    { name: 'Calendário', href: '/calendar', icon: Calendar, section: 'calendar' },
    { name: 'Localização', href: '/family-geo', icon: Navigation, section: 'family-geo' },
    { name: 'SOS', href: '/family-geo', icon: Heart, section: 'sos', isEmergency: true },
    { name: 'Mais', href: '/settings', icon: Settings, section: 'more' },
  ]

  if (!isMobile) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
      <div className="flex items-center justify-around py-2">
        {bottomNavItems.map((item) => {
          const Icon = item.icon
          const isActive = activeSection === item.section
          
          return (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setActiveSection(item.section)}
              className={`
                flex flex-col items-center space-y-1 px-2 py-2 rounded-lg transition-colors duration-200
                ${isActive 
                  ? 'text-blue-600' 
                  : item.isEmergency 
                    ? 'text-red-600' 
                    : 'text-gray-600'
                }
              `}
            >
              <Icon className={`w-5 h-5 ${item.isEmergency ? 'animate-pulse' : ''}`} />
              <span className="text-xs font-medium">{item.name}</span>
            </a>
          )
        })}
      </div>
    </div>
  )
}

// Componente de header responsivo
export const ResponsiveHeader = ({ title, showBackButton = false, onBackClick }) => {
  const { isMobile } = useResponsive()

  if (!isMobile) {
    return null
  }

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center space-x-3">
        {showBackButton && (
          <button
            onClick={onBackClick}
            className="p-1 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <h1 className="text-lg font-semibold text-gray-800 flex-1">{title}</h1>
      </div>
    </div>
  )
}

// Hook para gerenciar estado da navegação
export const useNavigation = () => {
  const { isMobile } = useResponsive()
  const { sidebarOpen, setSidebarOpen } = useSidebarVisibility()
  const [activeSection, setActiveSection] = useState('dashboard')

  const navigateTo = (section) => {
    setActiveSection(section)
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  return {
    activeSection,
    setActiveSection,
    navigateTo,
    toggleSidebar,
    sidebarOpen,
    setSidebarOpen,
    isMobile
  }
}

export default MobileNavigation
